import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Textarea } from "@arcadeai/design-system/components/ui/atoms/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useUpdateOrganization } from "@/app/organizations/api/hooks";
import {
  type EditOrganizationFormData,
  EditOrganizationSchema,
  type Organization,
} from "@/app/organizations/types";

type EditOrganizationDialogProps = {
  organization: Organization;
  setIsOpen: (isOpen: boolean) => void;
};

export function EditOrganizationDialog({
  organization,
  setIsOpen,
}: EditOrganizationDialogProps) {
  const { mutate: updateOrganization, isPending } = useUpdateOrganization();

  const form = useForm<EditOrganizationFormData>({
    resolver: zodResolver(EditOrganizationSchema),
    defaultValues: {
      name: organization.name,
      description: organization.description || "",
    },
  });

  // Reset form when organization changes
  useEffect(() => {
    form.reset({
      name: organization.name,
      description: organization.description || "",
    });
  }, [organization, form]);

  const onSubmit = (data: EditOrganizationFormData) => {
    updateOrganization(
      {
        organizationId: organization.organization_id,
        data: {
          name: data.name,
          description: data.description || undefined,
        },
      },
      {
        onSuccess: (updatedOrg) => {
          toast.success(`Successfully updated ${updatedOrg.name}`);
          setIsOpen(false);
          form.reset();
        },
        onError: (error) => {
          toast.error(
            `Failed to update organization: ${error.message || "An error occurred"}`
          );
        },
      }
    );
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogDescription>
          Update the organization name and description.
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization Name</FormLabel>
                <FormControl>
                  <Input
                    autoComplete="off"
                    placeholder="Enter organization name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (Optional)</FormLabel>
                <FormControl>
                  <Textarea
                    className="min-h-20 resize-none"
                    placeholder="Enter a brief description"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter className="flex">
            <Button
              disabled={isPending}
              onClick={() => setIsOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={isPending} type="submit">
              {isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}
